import { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, UserPlus, Building2, Save, KeyRound } from "lucide-react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  listCustomers, createCustomer, updateCustomer, deleteCustomer,
  getCustomer, sysadminCreateUser, sysadminResetPassword,
} from "@/lib/api";
import { toast } from "sonner";

const PLAN_TIERS = ["standard", "pro", "enterprise"];

function StatusPill({ status, expires }) {
  const isActive = (status || "active") === "active";
  const exp = expires ? new Date(expires) : null;
  const expired = exp && exp < new Date();
  const label = expired ? "Expired" : (isActive ? "Active" : "Suspended");
  const color = expired
    ? "bg-red-50 text-red-700 border-red-200"
    : isActive
    ? "bg-green-50 text-green-700 border-green-200"
    : "bg-amber-50 text-amber-700 border-amber-200";
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${color}`}>
      {label}
    </span>
  );
}

function CustomerForm({ initial = {}, onSubmit, submitting }) {
  const [form, setForm] = useState({
    name: initial.name || "",
    address: initial.address || "",
    phone: initial.phone || "",
    email: initial.email || "",
    plan_tier: initial.plan_tier || "standard",
    contract_start: initial.contract_start || "",
    expires_at: initial.expires_at || "",
    status: initial.status || "active",
    notes: initial.notes || "",
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  return (
    <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="text-xs text-zinc-500">School name</Label>
          <Input value={form.name} onChange={(e) => set("name", e.target.value)}
                 required className="mt-1.5" data-testid="customer-name-input" />
        </div>
        <div>
          <Label className="text-xs text-zinc-500">Email</Label>
          <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)}
                 className="mt-1.5" data-testid="customer-email-input" />
        </div>
        <div>
          <Label className="text-xs text-zinc-500">Phone</Label>
          <Input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                 className="mt-1.5" data-testid="customer-phone-input" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs text-zinc-500">Address</Label>
          <Input value={form.address} onChange={(e) => set("address", e.target.value)}
                 className="mt-1.5" data-testid="customer-address-input" />
        </div>
        <div>
          <Label className="text-xs text-zinc-500">Plan tier</Label>
          <select
            value={form.plan_tier}
            onChange={(e) => set("plan_tier", e.target.value)}
            className="mt-1.5 h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm"
            data-testid="customer-plan-input"
          >
            {PLAN_TIERS.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs text-zinc-500">Status</Label>
          <select
            value={form.status}
            onChange={(e) => set("status", e.target.value)}
            className="mt-1.5 h-10 w-full rounded-md border border-zinc-200 bg-white px-3 text-sm"
            data-testid="customer-status-input"
          >
            <option value="active">Active</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>
        <div>
          <Label className="text-xs text-zinc-500">Contract start</Label>
          <Input type="date" value={form.contract_start?.slice(0,10)}
                 onChange={(e) => set("contract_start", e.target.value)}
                 className="mt-1.5" data-testid="customer-contract-input" />
        </div>
        <div>
          <Label className="text-xs text-zinc-500">Expires at</Label>
          <Input type="datetime-local"
                 value={form.expires_at ? form.expires_at.slice(0, 16) : ""}
                 onChange={(e) => set("expires_at", e.target.value ? new Date(e.target.value).toISOString() : "")}
                 className="mt-1.5" data-testid="customer-expires-input" />
        </div>
        <div className="col-span-2">
          <Label className="text-xs text-zinc-500">Notes</Label>
          <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
                    className="mt-1.5 resize-none" rows={3}
                    data-testid="customer-notes-input" />
        </div>
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={submitting}
                data-testid="customer-save-btn"
                className="rounded-md bg-zinc-950 text-white hover:bg-zinc-800">
          {submitting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
          Save
        </Button>
      </div>
    </form>
  );
}

function CreateMainUserDialog({ customerId, open, onOpenChange, onCreated }) {
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await sysadminCreateUser({
        email: form.email, password: form.password, name: form.name,
        role: "tenant_admin", customer_id: customerId, status: "active",
      });
      toast.success("Main user created");
      onCreated();
      onOpenChange(false);
      setForm({ email: "", password: "", name: "" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create user");
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="main-user-dialog">
        <DialogHeader>
          <DialogTitle>Create main user</DialogTitle>
          <DialogDescription>
            This user will be the tenant administrator. They can create additional users inside their team.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label className="text-xs text-zinc-500">Full name</Label>
            <Input required value={form.name}
                   onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                   className="mt-1.5" data-testid="main-user-name-input" />
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Email</Label>
            <Input required type="email" value={form.email}
                   onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                   className="mt-1.5" data-testid="main-user-email-input" />
          </div>
          <div>
            <Label className="text-xs text-zinc-500">Temporary password</Label>
            <Input required type="text" value={form.password}
                   onChange={(e) => setForm(f => ({ ...f, password: e.target.value }))}
                   className="mt-1.5 font-mono" data-testid="main-user-password-input" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy}
                    data-testid="main-user-save-btn"
                    className="rounded-md bg-zinc-950 text-white hover:bg-zinc-800">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
              Create user
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ user, open, onOpenChange }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);

  const generate = () => {
    // 14-char alphanumeric — easy to type, strong enough for a one-shot reset
    const alpha = "ABCDEFGHJKMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789";
    const arr = new Uint32Array(14);
    crypto.getRandomValues(arr);
    setPassword(Array.from(arr, (n) => alpha[n % alpha.length]).join(""));
    setCopied(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await sysadminResetPassword(user.id, password);
      try { await navigator.clipboard.writeText(password); setCopied(true); } catch { /* ignore */ }
      toast.success(`Password reset for ${user.email} — share it securely`);
      onOpenChange(false);
      setPassword("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset password");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) { setPassword(""); setCopied(false); } }}>
      <DialogContent data-testid="reset-password-dialog">
        <DialogHeader>
          <DialogTitle>Reset password for {user?.name || user?.email}</DialogTitle>
          <DialogDescription>
            The user&apos;s current sessions will be signed out immediately. Share the new password over a secure channel.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs text-zinc-500">New password</Label>
              <button type="button" onClick={generate}
                      className="text-[11px] text-zinc-500 hover:text-zinc-950 underline underline-offset-2"
                      data-testid="reset-password-generate-btn">
                Generate secure
              </button>
            </div>
            <Input required type="text" value={password} minLength={8}
                   onChange={(e) => { setPassword(e.target.value); setCopied(false); }}
                   className="mt-1.5 font-mono"
                   placeholder="At least 8 characters"
                   data-testid="reset-password-input" />
            {copied ? (
              <div className="mt-1.5 text-[11px] text-green-700">Copied to clipboard.</div>
            ) : (
              <div className="mt-1.5 text-[11px] text-zinc-400">We&apos;ll copy it to your clipboard after save.</div>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={busy}
                    data-testid="reset-password-save-btn"
                    className="rounded-md bg-zinc-950 text-white hover:bg-zinc-800">
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
              Reset password
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function SysAdmin() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [userDlgOpen, setUserDlgOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { customers } = await listCustomers();
      setCustomers(customers);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { reload(); }, []);

  const loadDetail = async (id) => {
    setSelected(id); setDetail(null);
    try {
      const d = await getCustomer(id);
      setDetail(d);
    } catch { toast.error("Failed to load customer"); }
  };

  const onCreate = async (form) => {
    setSaving(true);
    try {
      const c = await createCustomer(form);
      toast.success(`${c.name} created`);
      setCreateOpen(false);
      await reload();
      loadDetail(c.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create");
    } finally { setSaving(false); }
  };

  const onUpdate = async (form) => {
    setSaving(true);
    try {
      await updateCustomer(selected, form);
      toast.success("Updated");
      setEditOpen(false);
      await reload();
      loadDetail(selected);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally { setSaving(false); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this customer and all its users? Components are kept but will 410.")) return;
    await deleteCustomer(id);
    toast.success("Deleted");
    setSelected(null); setDetail(null);
    reload();
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400 mb-3">Sys Admin</div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight font-semibold text-zinc-950">
              Customers
            </h1>
            <p className="text-zinc-600 mt-2 text-sm">
              One customer = one school. Create the customer, then provision the main user.
            </p>
          </div>
          <Button onClick={() => setCreateOpen(true)}
                  data-testid="sysadmin-new-customer-btn"
                  className="rounded-md bg-zinc-950 text-white hover:bg-zinc-800">
            <Plus className="h-4 w-4 mr-2" /> New customer
          </Button>
        </div>

        <div className="grid grid-cols-12 gap-6">
          <div className="col-span-12 lg:col-span-5">
            <div className="border border-zinc-200 bg-white rounded-md">
              {loading ? (
                <div className="h-40 grid place-items-center text-zinc-400">
                  <Loader2 className="h-5 w-5 animate-spin" />
                </div>
              ) : customers.length === 0 ? (
                <div className="p-6 text-center text-sm text-zinc-500">No customers yet.</div>
              ) : (
                <ul className="divide-y divide-zinc-100">
                  {customers.map((c) => (
                    <li key={c.id}>
                      <button
                        onClick={() => loadDetail(c.id)}
                        className={`w-full px-5 py-4 flex items-start gap-3 text-left hover:bg-zinc-50 ${
                          selected === c.id ? "bg-zinc-50" : ""
                        }`}
                        data-testid={`customer-row-${c.id}`}
                      >
                        <div className="h-9 w-9 rounded-md bg-zinc-950 text-white grid place-items-center flex-shrink-0">
                          <Building2 className="h-4 w-4" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-zinc-950 truncate">{c.name}</span>
                            <StatusPill status={c.status} expires={c.expires_at} />
                          </div>
                          <div className="text-xs text-zinc-500 mt-0.5 truncate">
                            {c.email || "no email"} · {c.user_count} users · {c.component_count} components
                          </div>
                        </div>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="col-span-12 lg:col-span-7">
            {!detail ? (
              <div className="border border-dashed border-zinc-300 bg-white rounded-md p-10 text-center text-sm text-zinc-500">
                Select a customer to see details.
              </div>
            ) : (
              <div className="space-y-4" data-testid="customer-detail-pane">
                <div className="border border-zinc-200 bg-white rounded-md p-6">
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div>
                      <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Customer</div>
                      <h2 className="font-display text-2xl font-semibold text-zinc-950">{detail.customer.name}</h2>
                      <div className="mt-1 flex gap-2 flex-wrap">
                        <StatusPill status={detail.customer.status} expires={detail.customer.expires_at} />
                        <Badge variant="secondary" className="text-[10px]">{detail.customer.plan_tier || "standard"}</Badge>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setEditOpen(true)}
                              data-testid="customer-edit-btn">Edit</Button>
                      <Button variant="outline" onClick={() => onDelete(detail.customer.id)}
                              data-testid="customer-delete-btn"
                              className="text-red-600 hover:text-red-700">
                        <Trash2 className="h-4 w-4 mr-1" /> Delete
                      </Button>
                    </div>
                  </div>
                  <dl className="grid grid-cols-2 gap-x-6 gap-y-3 mt-6 text-sm">
                    <div>
                      <dt className="text-xs uppercase tracking-wider text-zinc-400 font-bold">Email</dt>
                      <dd className="text-zinc-950">{detail.customer.email || "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wider text-zinc-400 font-bold">Phone</dt>
                      <dd className="text-zinc-950">{detail.customer.phone || "—"}</dd>
                    </div>
                    <div className="col-span-2">
                      <dt className="text-xs uppercase tracking-wider text-zinc-400 font-bold">Address</dt>
                      <dd className="text-zinc-950">{detail.customer.address || "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wider text-zinc-400 font-bold">Contract start</dt>
                      <dd className="text-zinc-950">{detail.customer.contract_start || "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs uppercase tracking-wider text-zinc-400 font-bold">Expires at</dt>
                      <dd className="text-zinc-950">
                        {detail.customer.expires_at
                          ? new Date(detail.customer.expires_at).toLocaleString()
                          : "Never"}
                      </dd>
                    </div>
                    {detail.customer.notes ? (
                      <div className="col-span-2">
                        <dt className="text-xs uppercase tracking-wider text-zinc-400 font-bold">Notes</dt>
                        <dd className="text-zinc-950 whitespace-pre-wrap">{detail.customer.notes}</dd>
                      </div>
                    ) : null}
                  </dl>
                </div>

                <div className="border border-zinc-200 bg-white rounded-md p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="text-xs tracking-[0.2em] uppercase font-bold text-zinc-400">Users</div>
                      <div className="font-display text-lg font-medium text-zinc-950">{detail.users.length}</div>
                    </div>
                    <Button variant="outline" onClick={() => setUserDlgOpen(true)}
                            data-testid="add-main-user-btn">
                      <UserPlus className="h-4 w-4 mr-2" /> Create main user
                    </Button>
                  </div>
                  {detail.users.length === 0 ? (
                    <p className="text-sm text-zinc-500">No users. Create the main user first.</p>
                  ) : (
                    <ul className="divide-y divide-zinc-100 border border-zinc-100 rounded-md">
                      {detail.users.map((u) => (
                        <li key={u.id} className="px-4 py-3 flex items-center gap-3"
                            data-testid={`sysadmin-user-row-${u.id}`}>
                          <div className="h-8 w-8 rounded-full bg-zinc-100 grid place-items-center text-xs font-semibold text-zinc-600">
                            {(u.name || u.email)[0]?.toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-medium text-zinc-950">{u.name || u.email}</div>
                            <div className="text-xs text-zinc-500">{u.email}</div>
                          </div>
                          <Badge variant="outline" className="text-[10px]">{u.role.replace("_", " ")}</Badge>
                          <StatusPill status={u.status} expires={u.expires_at} />
                          <Button variant="outline" size="sm"
                                  onClick={() => setResetTarget(u)}
                                  className="h-8 text-xs ml-1"
                                  data-testid={`reset-password-btn-${u.id}`}>
                            <KeyRound className="h-3.5 w-3.5 mr-1.5" /> Reset password
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-2xl" data-testid="customer-create-dialog">
          <DialogHeader>
            <DialogTitle>New customer</DialogTitle>
            <DialogDescription>Create the school record. You'll create the main user after.</DialogDescription>
          </DialogHeader>
          <CustomerForm onSubmit={onCreate} submitting={saving} />
        </DialogContent>
      </Dialog>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl" data-testid="customer-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit customer</DialogTitle>
          </DialogHeader>
          {detail ? <CustomerForm initial={detail.customer} onSubmit={onUpdate} submitting={saving} /> : null}
        </DialogContent>
      </Dialog>

      {selected ? (
        <CreateMainUserDialog
          customerId={selected}
          open={userDlgOpen}
          onOpenChange={setUserDlgOpen}
          onCreated={() => loadDetail(selected)}
        />
      ) : null}

      {resetTarget ? (
        <ResetPasswordDialog
          user={resetTarget}
          open={!!resetTarget}
          onOpenChange={(v) => { if (!v) setResetTarget(null); }}
        />
      ) : null}
    </div>
  );
}
